import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { getImageUrl } from "src/Common/common";

// top-level navigation tabs for the Calendar page.
// `path` is the URL slug under /calendar/:tab.
// `icon`/`activeIcon` are the inactive (black) and active (blue) variants.
export const CALENDAR_TABS = [
  {
    key: "workOrders",
    path: "work-orders",
    label: "Work Orders",
    icon: "/images/common/wo.svg",
    activeIcon: "/images/common/blue_wo.svg",
  },
  {
    key: "technicians",
    path: "technicians",
    label: "Technicians",
    icon: "/images/sidemenu/black_team.svg",
    activeIcon: "/images/common/blue_team.svg",
  },
  {
    key: "timeOff",
    path: "time-off",
    label: "Time Off",
    icon: "/images/common/clock.svg",
    activeIcon: "/images/common/blue_clock.svg",
  },
];

// default tab when /calendar is opened without a slug
export const DEFAULT_CALENDAR_TAB = CALENDAR_TABS[0];

// resolve a URL slug (/calendar/:tab) to a tab key; falls back to the default
export const tabKeyFromPath = (slug) =>
  (CALENDAR_TABS.find((t) => t.path === slug) ?? DEFAULT_CALENDAR_TAB).key;

// resolve a tab key to its URL slug
export const pathFromTabKey = (key) =>
  (CALENDAR_TABS.find((t) => t.key === key) ?? DEFAULT_CALENDAR_TAB).path;

// Underline tab bar with a smoothly-sliding active-tab indicator.
//
// The blue underline is rendered once and absolutely positioned; when
// `activeTab` changes we measure the target button's offset/width and
// let CSS transition both.
function CalendarTabbar({ activeTab, onChange }) {
  const tabRefs = useRef({});
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  // skip the transition until after the first measured paint, so the
  // indicator appears in place on load instead of sliding in from x=0
  const [animate, setAnimate] = useState(false);

  useLayoutEffect(() => {
    const el = tabRefs.current[activeTab];
    if (!el) return undefined;

    const measure = () => {
      setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    };
    measure();

    // re-measure when the button's size changes (font load, layout
    // shifts, window resize).
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeTab]);

  useEffect(() => {
    // enable the slide transition on the next frame, after the initial
    // measured position has painted.
    const id = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div className="relative inline-flex justify-start items-center gap-8">
      {/* animated active-tab underline */}
      <div
        className={`absolute bottom-0 h-0.5 bg-[#0058E5] ease-out pointer-events-none ${
          animate ? "transition-all duration-300" : ""
        }`}
        style={{ left: indicator.left, width: indicator.width }}
      />
      {CALENDAR_TABS.map((tab) => {
        const isActive = tab.key === activeTab;
        return (
          <button
            key={tab.key}
            ref={(el) => {
              tabRefs.current[tab.key] = el;
            }}
            onClick={() => onChange(tab.key)}
            style={{ outline: "none" }}
            className="relative z-10 h-11 flex justify-start items-center gap-2 focus:outline-none focus-visible:outline-none focus:ring-0"
          >
            <img
              className="w-4 h-4"
              src={getImageUrl(isActive ? tab.activeIcon : tab.icon)}
              alt=""
            />
            <div
              className={`text-center text-sm font-OpenSansBold leading-5 tracking-[0.14px] ${
                isActive ? "text-[#0058E5]" : "text-[#7C829A]"
              }`}
            >
              {tab.label}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default CalendarTabbar;
